<template>
  <div>
    <LocationsSelector
      :show-cities="true"
      :show-counties="true"
      :selected-locations="notification.formatted.params.locations"
      :read-only="readOnly"
      @change-locations="handleChangeLocations"
    ></LocationsSelector>

    <v-row>
      <v-col cols="6">
        <v-radio-group
          :value="notification.formatted.params.pulseTrigger"
          label="Pulse change triggers"
          hide-details
          class="mt-0 pt-0 pb-3"
          :disabled="readOnly"
          @change="handleChangePulseTrigger"
        >
          <v-radio value="below-current">
            <template v-slot:label>
              <div>Below specific value: <br />below current normal range</div>
            </template>
          </v-radio>
          <v-radio value="score-drop">
            <template v-slot:label>
              <div>
                <div class="pull-left drop-score-text">
                  Below specific value: <br />drop from current score
                </div>
                <div class="pull-left">
                  <v-text-field
                    v-show="selectedPulseTrigger === 'score-drop'"
                    v-model="selectedScoreDrop"
                    type="number"
                    :min="0"
                    :max="100"
                    height="12"
                    hide-details
                    class="pt-0 mt-0 ml-3 score-textbox"
                    @blur="handleBlurScoreDrop"
                  ></v-text-field>
                </div>
                <div class="clearfix"></div>
              </div>
            </template>
          </v-radio>
          <v-radio value="below-moving">
            <template v-slot:label>
              <div>Below moving normal range</div>
            </template>
          </v-radio>
        </v-radio-group>
      </v-col>
      <v-col class="grey--text">
        <div v-if="!selectedPulseTrigger">
          Pulse triggers allow you to be alerted of significant drops in
          stability so that you can monitor events proactively.
        </div>
        <div v-if="selectedPulseTrigger">
          <span class="application-heading">
            {{ selectedPulseTriggerName }}
          </span>
          - {{ selectedPulseTriggerDescription }}
        </div>
        <div class="mt-2">
          <a @click="handleClickLearnMore">Learn more</a>
        </div>
      </v-col>
    </v-row>

    <ConfirmationPopup
      :show="learnMoreShow"
      title="More About Pulse Triggers"
      :text="learnMoreShowText"
      :buttonCancel="learnMoreBtnCancel"
      :max-width="800"
    ></ConfirmationPopup>
  </div>
</template>

<script>
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";

import { MODULE_PREFIX_NOTIFICATIONS } from "@/store";
import { GET_NOTIFICATION } from "@/store/actions.type";
import { mapGetters } from "vuex";

export default {
  name: "NotificationsTripwiresPulse",
  components: {
    LocationsSelector: () => import("@/components/common/LocationsSelector"),
    ConfirmationPopup: () => import("@/components/common/ConfirmationPopup"),
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const pulseTriggers = [
      {
        text: "Below specific value: below current normal range",
        value: "below-current",
        description: `Will trigger an alert when Pulse goes below the current normal (average) range so you can be notified when stability conditions drop below the current range.`,
      },
      {
        text: "Below specific value: drop from current score",
        value: "score-drop",
        description: `Will trigger an alert when there is a selected point drop form today's score so that you can fine tune the sensitivity of Pulse change alerts.`,
      },
      {
        text: "Below moving normal range",
        value: "below-moving",
        description: `Will trigger an alert when Pulse goes below the future normal range (average) so you can be notified of sudden deteriorations in stability.`,
      },
    ];
    const scoreDropDefault = 5;
    const imgSrc = require("@/assets/images/SamplePulseDelta.png");
    const learnMoreExampleImg = `<img src="${imgSrc}" />`;
    const learnMoreShowText = `
      <h3 class="mb-5">Example of Pulse change trigger options to help you determine the best alert for you.</h3>
      <p>${learnMoreExampleImg}</p>
      <dl>
        <dt class="application-heading" style="text-decoration: underline;">Below specific value: below current normal range</dt>
          <dd>
            <p>In this case for Hungary, you will receive an alert when the Pulse score goes below 42, just outside of the current normal low range (Avg.).</p>
            <p>What can you expect: You will receive an alert every day that the score has dropped to below 42.</p>
          </dd>
        <dt class="application-heading" style="text-decoration: underline;">Below specific value: specified drop from moving current score</dt>
          <dd>
            <p>In this case for Hungary, you specified a 5 score value drop. You will receive an alert when the Pulse score drops to 44 or below.</p>
            <p>Setting a specified score drop allows you to be notified of Pulse changes that are significant to your organisation, both within and outside the location's normal range.</p>
            <p>What can you expect: You will receive an alert when the difference between yesterday's and today's score has dropped by 5 points.</p>
          </dd>
        <dt class="application-heading" style="text-decoration: underline;">Below moving normal range</dt>
          <dd>
            <p>In this case for Hungary, the normal range (a 60-day moving average) changes each day. You will receive an alert when the Pulse score falls below that day's normal range. For example, the normal range is 42-64 and the score falls to 39.</p>
            <p>This will notify you of sudden, sharp drops in the score, but you may miss a slow deterioration in conditions.</p>
            <p>What can you expect: You will receive an alert every day that the score has dropped to below the moving range.</p>
          </dd>
      </dl>
    `;

    return {
      pulseTriggers: pulseTriggers,
      selectedPulseTrigger: null,
      scoreDropDefault: scoreDropDefault,
      selectedScoreDrop: scoreDropDefault,
      learnMoreShow: false,
      learnMoreShowText: learnMoreShowText,
    };
  },
  computed: {
    ...mapGetters({
      notification: MODULE_PREFIX_NOTIFICATIONS + "notification",
    }),
    selectedPulseTriggerName() {
      if (this.selectedPulseTrigger) {
        const pulseTrigger = lodashFind(this.pulseTriggers, [
          "value",
          this.selectedPulseTrigger,
        ]);
        if (pulseTrigger) {
          return pulseTrigger.text;
        }
      }
      return null;
    },
    selectedPulseTriggerDescription() {
      if (this.selectedPulseTrigger) {
        const pulseTrigger = lodashFind(this.pulseTriggers, [
          "value",
          this.selectedPulseTrigger,
        ]);
        if (pulseTrigger) {
          return pulseTrigger.description;
        }
      }
      return null;
    },
    learnMoreBtnCancel() {
      return {
        text: "Close",
        onClickFnc: async () => {
          this.learnMoreShow = false;
        },
      };
    },
  },
  methods: {
    handleChangeLocations(locations) {
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      notification.formatted.params = notification.formatted.params || {};
      notification.formatted.params.locations = locations;
      this.update(notification);
    },
    handleChangePulseTrigger(value) {
      this.selectedPulseTrigger = value;
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      notification.formatted.params = notification.formatted.params || {};
      notification.formatted.params.pulseTrigger = value;
      if (notification.formatted.params.pulseTrigger === "score-drop") {
        notification.formatted.params.pulseScoreDrop = this.selectedScoreDrop;
      }
      this.update(notification);
    },
    handleBlurScoreDrop() {
      if (!this.selectedScoreDrop) {
        this.selectedScoreDrop = this.scoreDropDefault;
      }
      const notification = lodashCloneDeep(this.notification);
      notification.formatted = notification.formatted || {};
      notification.formatted.params = notification.formatted.params || {};
      if (notification.formatted.params.pulseTrigger === "score-drop") {
        notification.formatted.params.pulseScoreDrop = this.selectedScoreDrop;
      }
      this.update(notification);
    },
    update(notification) {
      this.$store.dispatch(
        MODULE_PREFIX_NOTIFICATIONS + GET_NOTIFICATION,
        notification
      );
    },
    handleClickLearnMore() {
      this.learnMoreShow = true;
    },
  },
  watch: {},
};
</script>

<style scoped>
.score-textbox {
  width: 40px;
  position: relative;
  top: 3px;
}
.drop-score-text {
  width: 140px;
}
</style>
